import useSWR from "swr";
import axios from "axios";
import { apiWobaBackend } from "@/services/api-woba-backend";
import { extractApiErrorMessage } from "@/utils/extract-api-error-message";
import { ProRataMemoryData } from "@/hooks/use-po-contract-prorata-memory/use-po-contract-prorata-memory.types";
import { FetchProRataMemoryPreviewProps } from "./use-po-contract-prorata-memory-preview.types";

const DEFAULT_ERROR_MESSAGE = "Não foi possível carregar a prévia do pró-rata.";

/**
 * Simula a memória de cálculo do pró-rata ANTES do POST de criação do
 * contrato de PO (PED-13781) — contraparte, para a CRIAÇÃO, do
 * `usePoContractProrataMemory` (que serve só a EDIÇÃO, já com `contractId`).
 * Usa `packageId/companyId/startDate/endDate/monthlyCredits/takePercentage`,
 * já disponíveis no formulário antes de confirmar, em vez de um contrato
 * persistido.
 *
 * SWR (CLAUDE.md: "SWR para data fetching"), não useState + fetch manual:
 * mesmo sendo um POST (o corpo não cabe numa querystring), os campos do
 * rascunho viram a key — `null` quando falta um campo obrigatório desliga o
 * fetch automaticamente, sem `useEffect` no chamador.
 *
 * Mesma postura de "não bloqueia o fluxo" do hook de edição: 404 (nenhum
 * ciclo cobre a data, ou pacote não encontrado) e qualquer outra falha só
 * fazem a prévia não aparecer — a criação continua funcionando normalmente.
 */
export const usePoContractProrataMemoryPreview = ({
  packageId,
  companyId,
  startDate,
  endDate,
  monthlyCredits,
  takePercentage,
}: Partial<FetchProRataMemoryPreviewProps>) => {
  const { data, error, isLoading } = useSWR<ProRataMemoryData | null>(
    packageId && companyId && startDate
      ? [
          "/private-offices/contracts/prorata-memory-preview",
          packageId,
          companyId,
          startDate,
          endDate,
          monthlyCredits,
          takePercentage,
        ]
      : null,
    async () => {
      try {
        const response = await apiWobaBackend.post(
          "/private-offices/contracts/prorata-memory-preview",
          {
            packageId,
            companyId,
            startDate,
            endDate,
            monthlyCredits,
            takePercentage,
          },
        );
        return response.data;
      } catch (fetchError: unknown) {
        if (
          axios.isAxiosError(fetchError) &&
          fetchError.response?.status === 404
        ) {
          return null;
        }

        console.error("Failed to fetch pro-rata memory preview:", fetchError);
        throw fetchError;
      }
    },
  );

  return {
    proRataMemory: data ?? null,
    isLoadingProRataMemory: isLoading,
    proRataMemoryError: error
      ? extractApiErrorMessage(error, DEFAULT_ERROR_MESSAGE)
      : null,
  };
};
